/**
 * The interval list doubles as the quantity picker: choosing "Køb 3 stk." has to put 3 into the
 * quantity the theme submits, otherwise the customer picks a saving and gets one item. The theme
 * owns that input, so the element writes into it rather than carrying its own.
 */
class CwQuantityTiers extends HTMLElement {
  connectedCallback() {
    this.addEventListener("change", this.#handleChange);
    this.#syncQuantity(this.#checkedQuantity());
  }

  disconnectedCallback() {
    this.removeEventListener("change", this.#handleChange);
  }

  #handleChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== "radio") return;

    for (const row of this.querySelectorAll(".cw-tiers__row")) {
      row.classList.toggle("is-selected", row.contains(input));
    }

    this.#syncQuantity(Number(input.value));
  };

  #checkedQuantity() {
    const checked = this.querySelector('input[type="radio"]:checked');
    return checked ? Number(checked.value) : 1;
  }

  /**
   * Themes render the quantity in different places, and some product forms leave it out when the
   * customer can only buy one. A hidden input inside the form covers that case, and it is kept in
   * the same form the button submits so a second product form on the page is left alone.
   */
  #syncQuantity(quantity) {
    if (!Number.isFinite(quantity) || quantity < 1) return;

    const form = this.#productForm();
    if (!form) return;

    const visible = form.querySelector('input[name="quantity"]:not([type="hidden"])');
    if (visible) {
      visible.value = String(quantity);
      visible.dispatchEvent(new Event("change", { bubbles: true }));
      return;
    }

    let hidden = form.querySelector('input[name="quantity"][data-cw-quantity]');
    if (!hidden) {
      hidden = document.createElement("input");
      hidden.type = "hidden";
      hidden.name = "quantity";
      hidden.dataset.cwQuantity = "true";
      form.appendChild(hidden);
    }
    hidden.value = String(quantity);
  }

  #productForm() {
    const section = this.closest(".shopify-section") ?? document;
    return (
      section.querySelector('form[action$="/cart/add"]') ??
      document.querySelector('form[action$="/cart/add"]')
    );
  }
}

if (!customElements.get("cw-quantity-tiers")) {
  customElements.define("cw-quantity-tiers", CwQuantityTiers);
}

/**
 * The combine row scrolls natively, so the arrows only nudge it by one card and keep
 * themselves in step with where the row sits. The scrollbar is hidden in CSS, which is why
 * the arrows matter on desktop: without them a mouse user has no visible affordance.
 */
class CwCombineCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector(".cw-combine__track");
    this.prev = this.querySelector(".cw-combine__nav--prev");
    this.next = this.querySelector(".cw-combine__nav--next");
    if (!this.track || !this.prev || !this.next) return;

    this.prev.addEventListener("click", () => this.#scrollBy(-1));
    this.next.addEventListener("click", () => this.#scrollBy(1));
    this.track.addEventListener("scroll", this.#sync, { passive: true });
    this.#resizeObserver = new ResizeObserver(this.#sync);
    this.#resizeObserver.observe(this.track);
    this.#sync();
  }

  disconnectedCallback() {
    this.track?.removeEventListener("scroll", this.#sync);
    this.#resizeObserver?.disconnect();
  }

  #resizeObserver;

  #step() {
    const card = this.track.querySelector(".cw-combine__item");
    if (!card) return this.track.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return card.getBoundingClientRect().width + gap;
  }

  /**
   * The native smooth scroll is dropped by the browser inside this snap container, so the
   * short glide is done by hand. Reduced motion gets the jump instead.
   */
  #scrollBy(direction) {
    const from = this.track.scrollLeft;
    const max = this.track.scrollWidth - this.track.clientWidth;
    const to = Math.max(0, Math.min(max, from + this.#step() * direction));
    if (to === from) return;

    // A hidden tab freezes requestAnimationFrame, so the row would sit still until the tab
    // came back. Same for reduced motion, both get the position straight away.
    if (
      document.visibilityState !== "visible" ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      this.track.scrollLeft = to;
      this.#sync();
      return;
    }

    const duration = 220;
    const start = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3);

    // Mandatory snapping latches onto the next card on the first frame and swallows the
    // animation, so snapping is off while the row is moving and back on once it lands.
    this.track.style.scrollSnapType = "none";

    const frame = (now) => {
      const progress = Math.min(1, (now - start) / duration);
      this.track.scrollLeft = from + (to - from) * ease(progress);
      if (progress < 1) {
        this.#animation = requestAnimationFrame(frame);
      } else {
        this.#animation = null;
        this.track.style.scrollSnapType = "";
        this.#sync();
      }
    };

    if (this.#animation) cancelAnimationFrame(this.#animation);
    this.#animation = requestAnimationFrame(frame);
  }

  #animation = null;

  #sync = () => {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const atStart = this.track.scrollLeft <= 1;
    const atEnd = this.track.scrollLeft >= max - 1;
    // Nothing to scroll means no arrows at all, rather than two dead buttons.
    this.prev.hidden = atStart || max <= 1;
    this.next.hidden = atEnd || max <= 1;
  };
}

if (!customElements.get("cw-combine-carousel")) {
  customElements.define("cw-combine-carousel", CwCombineCarousel);
}
